import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Copy, Check, Link2 } from 'lucide-react';
import { tokenomics } from '../data/mock';

const TokenomicsSection = () => {
  const [copied, setCopied] = useState(false);
  
  const tokenInfo = [
    { label: 'Token Name', value: tokenomics.tokenName },
    { label: 'Ticker', value: tokenomics.ticker },
    { label: 'Total Supply', value: tokenomics.totalSupply },
    { label: 'Blockchain', value: tokenomics.blockchain },
  ];

  const copyToClipboard = () => {
    navigator.clipboard.writeText(tokenomics.contractAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section id="tokenomics" className="relative py-24 sm:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute right-0 top-0 w-[400px] h-[400px] bg-orange-50/40 rounded-full blur-[100px]" />
        <div className="absolute left-0 bottom-0 w-[300px] h-[300px] bg-amber-50/30 rounded-full blur-[80px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-amber-500 font-semibold text-sm uppercase tracking-wider mb-4 block">Tokenomics</span>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight">
            Token Information
          </h2>
        </motion.div>

        {/* Token Info Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {tokenInfo.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -4 }}
              className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm"
            >
              <p className="text-gray-500 text-sm mb-2">{item.label}</p>
              <p className="text-2xl font-bold text-gray-900">{item.value}</p>
            </motion.div>
          ))}
        </div>

        {/* Contract Address Card - Centered */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="flex justify-center"
        >
          <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-3xl p-6 sm:p-8 max-w-2xl w-full">
            <div className="flex items-center gap-4 sm:gap-6">
              {/* Icon */}
              <div className="flex-shrink-0">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-amber-500 rounded-xl flex items-center justify-center">
                  <Link2 className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                </div>
              </div>
              
              {/* Address Content */}
              <div className="flex-1 min-w-0">
                <p className="text-gray-400 text-sm mb-1">Contract Address</p>
                <p className="text-white font-mono text-sm sm:text-base truncate">
                  {tokenomics.contractAddress}
                </p>
              </div>
              
              {/* Copy Button */}
              <div className="flex-shrink-0 relative">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={copyToClipboard}
                  className="w-12 h-12 bg-gray-700 hover:bg-gray-600 rounded-xl flex items-center justify-center transition-colors"
                >
                  {copied ? (
                    <Check size={20} className="text-green-400" />
                  ) : (
                    <Copy size={20} className="text-gray-300" />
                  )}
                </motion.button>
                
                {/* Copied Message */}
                <AnimatePresence>
                  {copied && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute -top-10 left-1/2 -translate-x-1/2 bg-green-500 text-white text-xs font-medium px-3 py-1.5 rounded-lg whitespace-nowrap"
                    >
                      Copied!
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TokenomicsSection;
